export interface NatureReportingColumnResponse {
  key: string;
  name: string;
  variants: string[];
  type?: string;
  description?: string;
}

export interface NatureReportingColumn {
  key: string;
  name: string;
  type?: string;
  description?: string;
}

export interface NatureReportingRowResponseMetrics {
  key: string;
  baseline?: string | number | boolean;
  future?: string | number | boolean;
  uplift?: string | number | boolean;
}

export interface NatureReportingRowResponse {
  id: string;
  declaration: string;
  organisation: string;
  map: string;
  holding: string;
  leadFarmer: string;
  landOwnership: string;
  mapName: string;
  produceTypes: string[];
  processors: string[];
  country: string[];
  county: string[];
  certificates?: LandDeclarationCertificates[];
  carbonAudit?: LandDeclarationCarbonAudit;
  baseLineArea?: string;
  acceptedAt: string;
  createdAt: string;
  metrics: NatureReportingRowResponseMetrics[];
}

export interface NatureReportingRow {
  [key: string]: string | number | boolean | undefined;
}

export interface NatureReportingTable {
  rows: NatureReportingRow[];
  columns: NatureReportingColumn[];
}

export interface Package {
  id: string;
  key: string;
  name: string;
  default?: boolean;
  columns: {
    key: string;
    name: string;
    type: string;
    variants: string[];
  }[];
}

export interface Processor {
  id: string;
  name: string;
}

export enum Anonymisation {
  NONE = 'NONE',
  FULL = 'FULL',
  OPTIONAL = 'OPTIONAL',
}

export interface NatureReporting {
  enabled: boolean;
  packages: string[];
  hasProcessors?: boolean;
  hasProduceTypes?: boolean;
  pricePerHa?: boolean;
  anonymisation: Anonymisation;
}

export enum LandDeclarationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
}

export enum AcceptanceStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  REVOKED = 'revoked',
}

export enum LandOwnership {
  OWNER = 'owner',
  TENANT = 'tenant',
  BOTH = 'both',
}

export enum LandDeclarationCertificates {
  RED_TRACTOR = 'red_tractor',
  LEAF_MARQUE = 'leaf_marque',
  FAWL = 'fawl',
}

export enum LandDeclarationCarbonAudit {
  NONE = 'none',
  FARM_CARBON_TOOLKIT = 'farm_carbon_toolkit',
  THE_COOL_FARM_TOOL = 'the_cool_farm_tool',
  AGRECALC = 'agrecalc',
  TRINITY_AG = 'trinity_ag',
  OTHER = 'other',
}

export enum NatureReportingValidation {
  GENERIC_ERROR = 'GENERIC_ERROR',
  GEOMETRY_ERROR = 'GEOMETRY_ERROR',
  OVERLAPPING_FEATURES = 'OVERLAPPING_FEATURES',
  OVERLAPPING_PLANS = 'OVERLAPPING_PLANS',
  UNASSIGNED_FEATURES = 'UNASSIGNED_FEATURES',
}

export interface LandDeclaration {
  id: string;
  map: string;
  status: LandDeclarationStatus;
  holding?: string;
  landOwnership?: LandOwnership;
  leadFarmer?: string;
  certificates?: LandDeclarationCertificates[];
  carbonAudit?: LandDeclarationCarbonAudit;
  plans?: {
    id: string;
    name: string;
    subtype: string;
  }[];
  publishedTo?: Partner[];
  metricsUpdatedAt?: Date;
  baseLineArea?: number;
  baseLineAreaCalculatedAt?: string;
  county?: string[];
  country?: string[];
  updatedAt?: Date;
  createdAt: Date;
}

export interface FarmDetails {
  holding: string;
  landOwnership: LandOwnership;
  leadFarmer: string;
  certificates?: LandDeclarationCertificates[];
  carbonAudit?: LandDeclarationCarbonAudit;
}

export interface LandDeclarationPublish {
  id: string;
  landDeclaration: Omit<LandDeclaration, 'publishedTo' | 'metricsUpdatedAt' | 'baseLineAreaCalculatedAt' | 'plans' | 'map'> & {
    plans: string[];
    map: { id: string; name: string };
  };
  organisation: string;
  status: AcceptanceStatus;
  processors?: string[];
  produceTypes?: string[];
  anonymisation?: boolean;
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
}

export interface LandDeclarationPublishParams {
  status: AcceptanceStatus;
  page?: number;
  size?: number;
  search?: string;
}

export interface Corporate {
  id: string;
  name: string;
  natureReporting: NatureReporting;
}

export interface PublishDetails {
  organisation: string;
  processors?: string[];
  produceTypes?: string[];
  anonymisation?: boolean;
}

export interface Partner {
  id: string;
  organisation: string;
  status: AcceptanceStatus;
  processors?: string[];
  produceTypes?: string[];
  anonymisation?: boolean;
  updatedAt?: Date;
  createdAt?: Date;
  history: {
    status: AcceptanceStatus;
    date: Date;
  }[];
}
export interface FarmerMetric {
  baseline?: number | string;
  key?: string;
  name?: string;
  type?: string;
  description?: string;
  future?: number;
  uplift?: number;
  color?: string;
  objective?: 'INCREASE' | 'DECREASE' | 'NEUTRAL';
  showMore?: boolean;
  featureCode?: string;
  featureName?: string;
  featureCategory?: string;
  geometryType?: 'Point' | 'LineString' | 'Polygon';
  habitatClassification?: {
    biodiversity?: boolean;
    productive?: boolean;
    distinctiveness?: 'VERY_HIGH' | 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW';
  };
  decimalPlaces?: number;
}
export interface Chart {
  type: 'CARDS' | 'LIST' | 'PIE' | 'FEATURE_CARDS' | 'UNASSIGNED_FEATURES' | 'OVERLAPPING_AREAS';
  key?: string;
  metrics?: FarmerMetric[];
  header?: string;
  subHeader?: string;
  showMore?: boolean;
  description?: string;
}
export interface FarmerMetricsSection {
  header: string;
  charts?: Chart[];
  description?: string;
  collapsible?: boolean;
  sections?: {
    charts: Chart[];
    header: string;
    description?: string;
  }[];
  seeMore?: boolean;
  key?: string;
}

export interface FarmerMetricLearnMore {
  baseline: number;
  description?: string;
  future?: number;
  key: string;
  name: string;
  objective?: 'INCREASE' | 'DECREASE' | 'NEUTRAL';
  paragraphs?: { title: string; content: string }[];
  type: string;
  uplift?: number;
  showMore?: boolean;
  links?: {
    url: string;
    label: string;
  }[];
  relatedMetrics?: FarmerMetric[];
}

export interface FarmerMetricsChartLearnMore {
  header: string;
  key: string;
  links: {
    url: string;
    label: string;
  }[];
  metrics: FarmerMetric[];
  subHeader: string;
  type: string;
  paragraphs: {
    title: string;
    content: string;
  }[];
}

export interface FarmerMetricsSectionSeeMore {
  header: string;
  key: string;
  charts?: Chart[];
  description?: string;
  links?: {
    label: string;
    url: string;
  }[];
  paragraphs?: [
    {
      title?: string;
      content?: string;
    },
  ];
}

export interface UnassignedFeaturesMetrics {
  plan: 'baseline' | 'future';
  metrics: { key: string; count: number; value: number }[];
}

export interface LandDeclarationValidationResults {
  baseline: {
    overlap: boolean;
    geometryError: boolean;
    overlappingPlans?: string[];
    unassignedFeatures: boolean;
    plansWithUnassignedFeatures?: string[];
  };
  future: {
    overlap: boolean;
    geometryError: boolean;
    overlappingPlans?: string[];
  };
}
